import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchMailbox, markMailAsRead, markMailAsUnread, deleteMailsBulk, moveMail } from "../api/mailApi";
import { loadLabels } from "../api/labelsApi";
import { loadSignatureSettings } from "../api/settingsApi";
import { AUTH_STORAGE_KEY } from "../socket";

export default function useMailbox(authHeaders) {
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [trash, setTrash] = useState([]);
  const [archive, setArchive] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [externalBlocked, setExternalBlocked] = useState(false);
  const [labelAssignments, setLabelAssignments] = useState({});
  const labelAssignmentsRef = useRef({});

  // load label assignments from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem("mail_label_assignments");
      const parsed = raw ? JSON.parse(raw) : {};
      const initial = parsed && typeof parsed === "object" ? parsed : {};
      setLabelAssignments(initial);
      labelAssignmentsRef.current = initial;
    } catch {
      setLabelAssignments({});
      labelAssignmentsRef.current = {};
    }
  }, []);

  const loadFolder = useCallback(
    async (folder) => {
      if (!authHeaders) return [];
      try {
        const mails = await fetchMailbox(folder, authHeaders);
        // attach label info from assignments (use ref to avoid recreating callback)
        const assignments = labelAssignmentsRef.current || {};
        const withLabels = Array.isArray(mails)
          ? mails.map((m) => ({ ...m, labels: Array.isArray(assignments[m.id]) ? [...assignments[m.id]] : [] }))
          : [];
        if (folder === "inbox") {
          setInbox(withLabels);
        } else if (folder === "sent") {
          setSent(withLabels);
        } else if (folder === "drafts" || folder === "draft") {
          setDrafts(withLabels);
        } else if (folder === "trash") {
          setTrash(withLabels);
        } else if (folder === "archive") {
          setArchive(withLabels);
        }
        return mails;
      } catch (err) {
        const message = err.response?.data?.error || `Failed to load ${folder}.`;
        setError(message);
        return [];
      }
    },
    [authHeaders]
  );

  useEffect(() => {
    if (!authHeaders) return;
    setLoading(true);
    (async () => {
      // External mail is always allowed (hardcoded at code level)
      setExternalBlocked(false);
      try {
        const results = await Promise.all([loadFolder("inbox"), loadFolder("sent"), loadFolder("drafts"), loadFolder("trash"), loadFolder("archive")]);
        const [inboxMails = [], sentMails = [], draftsMails = [], trashMails = [], archiveMails = []] = results || [];

        // Load label rules from server (user-scoped)
        let rules = [];
        try {
          const res = await loadLabels(authHeaders);
          const list = Array.isArray(res?.labels) ? res.labels : [];
          rules = list.filter((l) => l && l.emailMatch && String(l.emailMatch).trim());
        } catch (e) {
          rules = [];
        }

        if (rules.length) {
          const next = { ...(labelAssignments || {}) };
          const folders = [inboxMails, sentMails, draftsMails, trashMails];
          rules.forEach((rule) => {
            const em = String(rule.emailMatch).trim().toLowerCase();
            folders.forEach((arr) => {
              if (!Array.isArray(arr)) return;
              arr.forEach((m) => {
                if (!m || !m.id || !m.from) return;
                if (String(m.from).trim().toLowerCase() === em) {
                  const existing = Array.isArray(next[m.id]) ? new Set(next[m.id]) : new Set();
                  existing.add(String(rule.id));
                  next[m.id] = Array.from(existing);
                }
              });
            });
          });
          // persist and set
          try {
            localStorage.setItem("mail_label_assignments", JSON.stringify(next));
          } catch (e) {}
          labelAssignmentsRef.current = next;
          setLabelAssignments(next);
          // update states: add labels into mail objects and remove labeled mails from inbox view
          const annotate = (arr) => (Array.isArray(arr) ? arr.map((m) => ({ ...m, labels: Array.isArray(next[m.id]) ? [...next[m.id]] : [] })) : []);
          // keep labeled mails visible in the inbox rather than filtering them out
          setInbox(annotate(inboxMails));
          setSent(annotate(sentMails));
          setDrafts(annotate(draftsMails));
          setTrash(annotate(trashMails));
          setArchive(annotate(archiveMails));
        }
      } catch (err) {
        // ignore rule-apply errors
      } finally {
        setLoading(false);
      }
    })();
  }, [authHeaders, loadFolder]);

  // Listen for real-time incoming mail events (dispatched from `socket.js`)
  useEffect(() => {
    if (!authHeaders) return;
    const handler = (ev) => {
      try {
        const payload = ev?.detail;
        if (!payload || !payload.id) return;
        // Attach any labels from assignments
        const assignments = labelAssignmentsRef.current || {};
        const labels = Array.isArray(assignments[payload.id]) ? [...assignments[payload.id]] : [];
        const mailWithLabels = { ...(payload || {}), labels };
        // Prepend to inbox
        setInbox((prev) => [mailWithLabels, ...prev]);
      } catch (e) {}
    };
    window.addEventListener("mail-received", handler);

    // updated mail handler
    const updatedHandler = (ev) => {
      try {
        const payload = ev?.detail;
        if (!payload || !payload.id) return;
        const assignments = labelAssignmentsRef.current || {};
        const labels = Array.isArray(assignments[payload.id]) ? [...assignments[payload.id]] : [];
        const mailWithLabels = { ...(payload || {}), labels };
        // Replace/update mail across folders
        setInbox((prev) => prev.map((m) => (m && m.id === mailWithLabels.id ? { ...m, ...mailWithLabels } : m)));
        setSent((prev) => prev.map((m) => (m && m.id === mailWithLabels.id ? { ...m, ...mailWithLabels } : m)));
        setDrafts((prev) => prev.map((m) => (m && m.id === mailWithLabels.id ? { ...m, ...mailWithLabels } : m)));
        setTrash((prev) => prev.map((m) => (m && m.id === mailWithLabels.id ? { ...m, ...mailWithLabels } : m)));
        setArchive((prev) => prev.map((m) => (m && m.id === mailWithLabels.id ? { ...m, ...mailWithLabels } : m)));
      } catch (e) {}
    };

    window.addEventListener("mail-updated", updatedHandler);

    return () => {
      window.removeEventListener("mail-received", handler);
      window.removeEventListener("mail-updated", updatedHandler);
    };
  }, [authHeaders]);

  // reapply rules when labels change (so users saving a new rule takes effect)
  useEffect(() => {
    const handler = () => {
      if (!authHeaders) return;
      void Promise.all([loadFolder("inbox"), loadFolder("sent"), loadFolder("drafts"), loadFolder("trash")]);
    };
    window.addEventListener("labels-updated", handler);
    return () => window.removeEventListener("labels-updated", handler);
  }, [authHeaders, loadFolder]);

  const unreadCount = useMemo(() => inbox.filter((mail) => mail.unread).length, [inbox]);

  const markAsRead = useCallback(
    async (mailId) => {
      if (!authHeaders) return;
      try {
        await markMailAsRead(mailId, authHeaders);
        setInbox((prev) => prev.map((mail) => (mail.id === mailId ? { ...mail, unread: false } : mail)));
      } catch {}
    },
    [authHeaders]
  );

  const markAsUnread = useCallback(
    async (mailId) => {
      if (!authHeaders) return;
      try {
        await markMailAsUnread(mailId, authHeaders);
        setInbox((prev) => prev.map((mail) => (mail.id === mailId ? { ...mail, unread: true } : mail)));
      } catch {}
    },
    [authHeaders]
  );

const removeMails = useCallback(
  async (ids = []) => {
    if (!Array.isArray(ids) || !ids.length) return;

    if (authHeaders) {
      try {
        const res = await deleteMailsBulk(ids, authHeaders);
        if (!res?.success) throw new Error("Delete failed");
      } catch (err) {
        console.error("Delete failed:", err);
        return; // ❗ stop here if backend failed
      }
    }

    setInbox((prev) => prev.filter((m) => !ids.includes(m.id)));
    setSent((prev) => prev.filter((m) => !ids.includes(m.id)));
    setDrafts((prev) => prev.filter((m) => !ids.includes(m.id)));
    setTrash((prev) => prev.filter((m) => !ids.includes(m.id)));
    setArchive((prev) => prev.filter((m) => !ids.includes(m.id)));

    // Reload folders from server to ensure counts and UI are in sync
    if (authHeaders) {
      // best-effort: refresh main folders
      try {
        void Promise.all([loadFolder("inbox"), loadFolder("sent"), loadFolder("drafts"), loadFolder("trash")]);
      } catch (err) {
        // ignore
      }
    }
  },
  [authHeaders]
);



  const moveMails = useCallback(
    async (ids = [], toFolder) => {
      if (!Array.isArray(ids) || !ids.length) return;
      if (toFolder !== "inbox" && toFolder !== "sent" && toFolder !== "draft" && toFolder !== "trash" && toFolder !== "archive") return;
      if (authHeaders) {
        try {
          await Promise.all(ids.map((id) => moveMail(id, toFolder, authHeaders)));
        } catch (err) {
          // continue to update locally
        }
      }

      // update local state
      setInbox((prev) => prev.filter((m) => !ids.includes(m.id)));
      setSent((prev) => prev.filter((m) => !ids.includes(m.id)));
      setDrafts((prev) => prev.filter((m) => !ids.includes(m.id)));
      setTrash((prev) => prev.filter((m) => !ids.includes(m.id)));
      setArchive((prev) => prev.filter((m) => !ids.includes(m.id)));
      // For simplicity, reload target folder from server if authenticated
      if (authHeaders) {
        try {
          // refresh both the source (inbox/sent/drafts) and the destination (toFolder)
          void Promise.all([loadFolder("inbox"), loadFolder("sent"), loadFolder("drafts"), loadFolder("trash"), loadFolder("archive")]);
        } catch (err) {
          // ignore
        }
      }
    },
    [authHeaders, loadFolder]
  );

    const persistAssignments = (next) => {
      try {
        localStorage.setItem("mail_label_assignments", JSON.stringify(next));
      } catch (e) {}
    };

    const applyLabelToMails = useCallback((ids = [], labelId) => {
      if (!Array.isArray(ids) || !ids.length || !labelId) return;
      setLabelAssignments((prev) => {
          const next = { ...prev };
        ids.forEach((id) => {
          const list = Array.isArray(next[id]) ? new Set(next[id]) : new Set();
          list.add(labelId);
          next[id] = Array.from(list);
        });
          persistAssignments(next);
          labelAssignmentsRef.current = next;
          // update mailbox states
        setInbox((prevInbox) => prevInbox.map((m) => (ids.includes(m.id) ? { ...m, labels: Array.from(new Set([...(m.labels||[]), labelId])) } : m)));
        setSent((prevSent) => prevSent.map((m) => (ids.includes(m.id) ? { ...m, labels: Array.from(new Set([...(m.labels||[]), labelId])) } : m)));
        setDrafts((prevDrafts) => prevDrafts.map((m) => (ids.includes(m.id) ? { ...m, labels: Array.from(new Set([...(m.labels||[]), labelId])) } : m)));
        return next;
      });
    }, []);

    const removeLabelFromMails = useCallback((ids = [], labelId) => {
      if (!Array.isArray(ids) || !ids.length || !labelId) return;
      setLabelAssignments((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          const list = Array.isArray(next[id]) ? next[id].filter((l) => l !== labelId) : [];
          if (list.length) next[id] = list; else delete next[id];
        });
        persistAssignments(next);
        labelAssignmentsRef.current = next;
        setInbox((prevInbox) => prevInbox.map((m) => (ids.includes(m.id) ? { ...m, labels: (m.labels||[]).filter((l) => l !== labelId) } : m)));
        setSent((prevSent) => prevSent.map((m) => (ids.includes(m.id) ? { ...m, labels: (m.labels||[]).filter((l) => l !== labelId) } : m)));
        setDrafts((prevDrafts) => prevDrafts.map((m) => (ids.includes(m.id) ? { ...m, labels: (m.labels||[]).filter((l) => l !== labelId) } : m)));
        return next;
      });
    }, []);

  const appendSentMail = useCallback((mail) => {
    if (!mail) return;
    setSent((prev) => [mail, ...prev]);
  }, []);

  const updateMail = useCallback((updatedMail) => {
    if (!updatedMail || !updatedMail.id) return;
    setInbox((prev) => prev.map((m) => (m && m.id === updatedMail.id ? { ...m, ...updatedMail } : m)));
    setSent((prev) => prev.map((m) => (m && m.id === updatedMail.id ? { ...m, ...updatedMail } : m)));
    setDrafts((prev) => prev.map((m) => (m && m.id === updatedMail.id ? { ...m, ...updatedMail } : m)));
    setTrash((prev) => prev.map((m) => (m && m.id === updatedMail.id ? { ...m, ...updatedMail } : m)));
    setArchive((prev) => prev.map((m) => (m && m.id === updatedMail.id ? { ...m, ...updatedMail } : m)));
  }, []);

  return {
    inbox,
    sent,
    drafts,
    trash,
    archive,
    loading,
    error,
    externalBlocked,
    unreadCount,
    loadFolder,
    markAsRead,
    markAsUnread,
    removeMails,
    moveMails,
    applyLabelToMails,
    removeLabelFromMails,
    appendSentMail,
    updateMail,
  };
}
